import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, idempotencyKey, unwrap } from '@/api/client'
import type { Schemas } from '@/api/types'
import { orderKeys } from '@/features/orders/queries'

export type StageCommand = 'accept' | 'start' | 'resume' | 'complete' | 'transfer' | 'skip'

const PATHS = {
  accept: '/api/v1/order-stages/{id}/accept',
  start: '/api/v1/order-stages/{id}/start',
  resume: '/api/v1/order-stages/{id}/resume',
  complete: '/api/v1/order-stages/{id}/complete',
  transfer: '/api/v1/order-stages/{id}/transfer',
  skip: '/api/v1/order-stages/{id}/skip',
} as const

function useStageInvalidate(orderId: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: orderKeys.stages(orderId) })
    void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
    void qc.invalidateQueries({ queryKey: orderKeys.history(orderId) })
  }
}

/** accept/start/resume/complete/transfer/skip — share an optional command body. */
export function useStageCommand(orderId: string) {
  const invalidate = useStageInvalidate(orderId)
  return useMutation({
    mutationFn: (vars: { stageId: string; command: StageCommand; version: number; reason?: string }) =>
      unwrap(
        api.POST(PATHS[vars.command], {
          params: {
            path: { id: vars.stageId },
            header: { 'Idempotency-Key': idempotencyKey() },
          },
          body: { version: vars.version, reason: vars.reason },
        }),
      ),
    onSuccess: invalidate,
  })
}

export function usePauseStage(orderId: string) {
  const invalidate = useStageInvalidate(orderId)
  return useMutation({
    mutationFn: (vars: { stageId: string; version: number; delay_reason_id: string }) =>
      unwrap(
        api.POST('/api/v1/order-stages/{id}/pause', {
          params: {
            path: { id: vars.stageId },
            header: { 'Idempotency-Key': idempotencyKey() },
          },
          body: { version: vars.version, delay_reason_id: vars.delay_reason_id },
        }),
      ),
    onSuccess: invalidate,
  })
}

export function useAssignStage(orderId: string) {
  const invalidate = useStageInvalidate(orderId)
  return useMutation({
    mutationFn: (vars: { stageId: string; version: number; assignee_id: string }) =>
      unwrap(
        api.POST('/api/v1/order-stages/{id}/assign', {
          params: {
            path: { id: vars.stageId },
            header: { 'Idempotency-Key': idempotencyKey() },
          },
          body: { assignee_id: vars.assignee_id, version: vars.version },
        }),
      ),
    onSuccess: invalidate,
  })
}

export function useAdminCorrectStage(orderId: string) {
  const invalidate = useStageInvalidate(orderId)
  return useMutation({
    mutationFn: (vars: { stageId: string; body: Schemas['StageAdminCorrectRequest'] }) =>
      unwrap(
        api.POST('/api/v1/order-stages/{id}/admin-correct', {
          params: {
            path: { id: vars.stageId },
            header: { 'Idempotency-Key': idempotencyKey() },
          },
          body: vars.body,
        }),
      ),
    onSuccess: invalidate,
  })
}
